import { Autocomplete, CircularProgress, TextField, Box, Typography, Tooltip } from '@mui/material'
import { Props } from './props'
import React, { SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomPaper } from '../../CustomPaper'
import { Item } from '../../../utils/types/generals'

const normalizeHex = (hex?: string | null) => {
  if (!hex) return undefined
  const clean = `${hex}`.trim().replace('#', '')
  if (!clean) return undefined
  return `#${clean}`
}

const MultiSelectSimple = (props: Props) => {
  const { t } = useTranslation()

  const handleChange = (_: SyntheticEvent<Element, Event>, value: Item | null) => {
    props.onChange({
      target: {
        name: props.name,
        value: value || undefined,
      },
    })
  }

  const selectedOption =
    (props.items as any[])?.find((item: any) => item.value == (props.selected?.value ?? props.selected)) ?? null
  const selectedStatusName = (selectedOption as any)?.storeStatusName
  const selectedStatusColor = normalizeHex((selectedOption as any)?.storeStatusColor)

  return (
    <Autocomplete
      key={props.name}
      sx={props.sx}
      disableClearable={props?.disableClearable}
      disabled={props.disabled}
      noOptionsText={t('general.noOptions')}
      options={props.items || []}
      getOptionLabel={(option: any) => {
        const statusName = (option as any)?.storeStatusName
        return statusName ? `${option.label ?? ''} - ${statusName}` : option.label ?? ''
      }}
      value={selectedOption}
      filterSelectedOptions
      autoComplete={false}
      onChange={handleChange}
      PaperComponent={CustomPaper}
      renderOption={(liProps, option: any) => {
        const statusName = option?.storeStatusName
        const statusColor = normalizeHex(option?.storeStatusColor)
        return (
          <li {...liProps} key={option.value}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
              <Typography
                className="custom-lines"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {option.label}
                {!!statusName && (
                  <Typography
                    component="span"
                    sx={{
                      color: statusColor ?? '#828282',
                      ml: 0.5,
                    }}
                  >
                    {`- ${statusName}`}
                  </Typography>
                )}
              </Typography>
            </Box>
          </li>
        )
      }}
      renderInput={(params) => {
        const { inputProps, ...restParams } = params
        const displayValue = inputProps?.value as string

        return (
          <TextField
            {...restParams}
            name={props.name}
            label={props.label}
            helperText={
              (props?.errors?.[props?.name]?.translate
                ? t(props?.errors?.[props?.name]?.translate)
                : props?.errors?.[props?.name]) ?? ''
            }
            error={props?.errors?.[props?.name]?.translate ?? props?.errors?.[props?.name]}
            inputProps={{
              ...inputProps,
              value: displayValue,
              readOnly: !!selectedStatusName,
              onMouseDown: (e: React.MouseEvent<HTMLInputElement>) => {
                if (selectedStatusName) {
                  e.preventDefault()
                }
                inputProps?.onMouseDown?.(e as any)
              },
              onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                if (selectedStatusName) {
                  e.target.blur()
                  e.preventDefault()
                }
                inputProps?.onFocus?.(e as any)
              },
            }}
            InputProps={{
              ...restParams.InputProps,
              sx: {
                '& input': {
                  color: selectedStatusColor ? 'transparent' : 'inherit',
                  userSelect: 'none',
                  cursor: 'pointer',
                  caretColor: 'transparent',
                },
              },
              startAdornment: selectedStatusName ? (
                <Tooltip title={`${selectedOption?.label} - ${selectedStatusName}`}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      position: 'absolute',
                      left: 14,
                      right: 60,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      //pointerEvents: 'none',
                      userSelect: 'none',
                    }}
                  >
                    <Typography sx={{ color: 'text.primary', userSelect: 'none' }}>
                      {selectedOption?.label}
                    </Typography>
                    <Typography
                      sx={{
                        color: selectedStatusColor ?? '#828282',
                        userSelect: 'none',
                      }}
                    >
                      {`- ${selectedStatusName}`}
                    </Typography>
                  </Box>
                </Tooltip>
              ) : null,
              endAdornment: (
                <React.Fragment>
                  {props?.loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {restParams.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )
      }}
    />
  )
}

export default MultiSelectSimple